import { Spinner } from '../components/feedback';
// スケジュール（定期再起動・時刻指定のコマンド・定期スナップショット）と、自動起動・クラッシュ時の自動再起動の設定。
import { useState, type FormEvent } from 'react';
import { apiM } from '../api/client-maint';
import type { ServerView } from '../api/types';
import type { ScheduledTask, ScheduledTaskInput, ServerPolicy, TaskRunResult } from '../api/types-maint';
import { MAX_SCHEDULED_SNAPSHOTS } from '@mcpanel/core';
import { errorMessage, useApp } from '../app-context';
import { Badge, Dialog, Empty, ErrorAlert } from '../components/ui';
import { formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';

const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

export const runResultLabel: Record<TaskRunResult, { label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' }> = {
  restarted: { label: '再起動した', tone: 'ok' },
  executed: { label: '実行した', tone: 'ok' },
  snapshotted: { label: 'スナップショットを取った', tone: 'ok' },
  skipped_players: { label: 'プレイヤーがいたので見送り', tone: 'muted' },
  skipped_busy: { label: '別の操作中だったので見送り', tone: 'warn' },
  skipped_stopped: { label: '停止中だったので見送り', tone: 'muted' },
  failed: { label: '失敗', tone: 'danger' },
};

function daysText(days: number[]) {
  if (days.length === 0 || days.length === 7) return '毎日';
  const sorted = [...days].sort();
  if (sorted.join() === '1,2,3,4,5') return '平日';
  if (sorted.join() === '0,6') return '土日';
  return `毎週 ${sorted.map((d) => dayNames[d]).join('・')}`;
}

const defaultName: Record<ScheduledTaskInput['kind'], string> = {
  restart: '定期再起動',
  command: 'コマンド',
  snapshot: '定期スナップショット',
};

const blankTask: ScheduledTaskInput = {
  name: '',
  enabled: true,
  kind: 'restart',
  time: '05:00',
  days: [],
  commands: [],
  warnMinutes: [10, 5, 1],
  skipIfPlayersOnline: false,
  keep: 7,
};

function TaskEditor({
  serverId,
  initial,
  onClose,
  onSaved,
}: {
  serverId: string;
  initial?: ScheduledTask;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useApp();
  const [form, setForm] = useState<ScheduledTaskInput>(
    initial
      ? {
          name: initial.name,
          enabled: initial.enabled,
          kind: initial.kind,
          time: initial.time,
          days: initial.days,
          commands: initial.commands,
          warnMinutes: initial.warnMinutes,
          skipIfPlayersOnline: initial.skipIfPlayersOnline,
          keep: initial.keep,
        }
      : blankTask,
  );
  const [commands, setCommands] = useState(form.commands.join('\n'));
  const [warn, setWarn] = useState(form.warnMinutes.join(', '));
  const [busy, setBusy] = useState(false);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const input: ScheduledTaskInput = {
      ...form,
      name: form.name.trim() || defaultName[form.kind],
      commands:
        form.kind === 'command'
          ? commands
              .split('\n')
              .map((c) => c.trim())
              .filter(Boolean)
          : [],
      warnMinutes:
        form.kind === 'restart'
          ? [
              ...new Set(
                warn
                  .split(/[,\s、]+/)
                  .map(Number)
                  .filter((n) => Number.isInteger(n) && n >= 1 && n <= 60),
              ),
            ].sort((a, b) => b - a)
          : [],
    };
    setBusy(true);
    try {
      if (initial) await apiM.updateTask(serverId, initial.id, input);
      else await apiM.createTask(serverId, input);
      notify('スケジュールを保存しました');
      onSaved();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (day: number) =>
    setForm({ ...form, days: form.days.includes(day) ? form.days.filter((d) => d !== day) : [...form.days, day] });

  return (
    <Dialog
      title={initial ? 'スケジュールを編集' : 'スケジュールを追加'}
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            キャンセル
          </button>
          <button
            className="btn primary"
            form="task-form"
            disabled={
              busy ||
              (form.kind === 'snapshot' &&
                (!Number.isInteger(form.keep) || form.keep < 1 || form.keep > MAX_SCHEDULED_SNAPSHOTS))
            }
          >
            {busy && <Spinner />}
            保存
          </button>
        </>
      }
    >
      <form id="task-form" className="stack" style={{ gap: 14 }} onSubmit={(event) => void save(event)}>
        <div className="row" role="radiogroup" aria-label="種類">
          {(
            [
              ['restart', '再起動'],
              ['command', 'コマンドを実行'],
              ['snapshot', 'スナップショット'],
            ] as const
          ).map(([kind, label]) => (
            <label key={kind} className="check">
              <input
                type="radio"
                name="kind"
                checked={form.kind === kind}
                onChange={() => setForm({ ...form, kind })}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="field">
          <label htmlFor="task-name">名前</label>
          <input
            id="task-name"
            className="input"
            value={form.name}
            maxLength={60}
            placeholder={
              { restart: '毎朝の再起動', command: '夜のお知らせ', snapshot: '毎日のバックアップ' }[form.kind]
            }
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="task-time">時刻（日本時間）</label>
            <input
              id="task-time"
              className="input"
              type="time"
              value={form.time}
              required
              onChange={(e) => setForm({ ...form, time: e.target.value })}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>曜日（選ばなければ毎日）</label>
            <div className="row" style={{ gap: 4 }}>
              {dayNames.map((name, day) => (
                <button
                  key={name}
                  type="button"
                  className={`btn small${form.days.includes(day) ? ' primary' : ''}`}
                  aria-pressed={form.days.includes(day)}
                  onClick={() => toggleDay(day)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>
        {form.kind === 'snapshot' ? (
          <div className="field">
            <label htmlFor="task-keep">残す数（これより古い定期スナップショットから消す）</label>
            <input
              id="task-keep"
              className="input"
              type="number"
              min={1}
              max={MAX_SCHEDULED_SNAPSHOTS}
              style={{ width: 90 }}
              value={form.keep}
              onChange={(e) => setForm({ ...form, keep: Number(e.target.value) })}
            />
            {(!Number.isInteger(form.keep) || form.keep < 1 || form.keep > MAX_SCHEDULED_SNAPSHOTS) && (
              <div role="alert" style={{ color: 'var(--danger)' }}>
                1〜{MAX_SCHEDULED_SNAPSHOTS} の整数を入力してください。
              </div>
            )}
            <div className="small faint">
              VM
              ごと取るので、ワールド・プラグインの設定・データがすべて入ります。数秒だけディスクへの書き込みを止めます。
              手動や変更前のスナップショットは消しません。停止中でも取ります。
            </div>
          </div>
        ) : form.kind === 'command' ? (
          <div className="field">
            <label htmlFor="task-commands">コマンド（1 行に 1 つ。先頭の / は不要。上から順に実行）</label>
            <textarea
              id="task-commands"
              className="input"
              style={{ height: 110, padding: 8, fontFamily: 'var(--mono)' }}
              value={commands}
              onChange={(e) => setCommands(e.target.value)}
              placeholder={'say まもなくイベントが始まります\ntime set day'}
            />
            <div className="small faint">サーバーが停止中なら、その回は実行しません。</div>
          </div>
        ) : (
          <>
            <div className="field">
              <label htmlFor="task-warn">何分前にゲーム内で予告するか（カンマ区切り）</label>
              <input
                id="task-warn"
                className="input"
                value={warn}
                onChange={(e) => setWarn(e.target.value)}
                placeholder="10, 5, 1"
              />
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={form.skipIfPlayersOnline}
                onChange={(e) => setForm({ ...form, skipIfPlayersOnline: e.target.checked })}
              />
              接続中のプレイヤーがいたら、その回は再起動しない
            </label>
          </>
        )}
        <label className="check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          有効
        </label>
      </form>
    </Dialog>
  );
}

function idleVmMinutesValid(minutes: number) {
  return Number.isInteger(minutes) && minutes >= 0 && minutes <= 1440;
}

function PolicyCard({ serverId }: { serverId: string }) {
  const { canOperate, notify } = useApp();
  const loaded = usePoll(() => apiM.policy(serverId), 0, [serverId]);
  const [draft, setDraft] = useState<ServerPolicy>();
  const policy = draft ?? loaded.data;

  const save = async () => {
    if (!draft) return;
    try {
      await apiM.setPolicy(serverId, draft);
      notify('自動化の設定を保存しました');
      setDraft(undefined);
      await loaded.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  return (
    <section className="card card-body stack" style={{ gap: 10 }}>
      <h2>自動起動と自動復旧</h2>
      <ErrorAlert error={loaded.error} />
      {policy && (
        <>
          <label className="check">
            <input
              type="checkbox"
              disabled={!canOperate}
              checked={policy.autoStart}
              onChange={(e) => setDraft({ ...policy, autoStart: e.target.checked })}
            />
            ノード（VM）が起動したら、Minecraft も自動で起動する
          </label>
          <div className="small faint" style={{ paddingLeft: 26 }}>
            手動で停止したサーバーは、停電やノードの再起動のあとも止まったままにします。
          </div>
          <label className="check">
            <input
              type="checkbox"
              disabled={!canOperate}
              checked={policy.autoRestart}
              onChange={(e) => setDraft({ ...policy, autoRestart: e.target.checked })}
            />
            クラッシュしたら自動で再起動する
          </label>
          {policy.autoRestart && (
            <div className="row small" style={{ paddingLeft: 26 }}>
              <div className="field">
                <label htmlFor="policy-window">期間（分）</label>
                <input
                  id="policy-window"
                  className="input"
                  type="number"
                  aria-label="期間（分）"
                  min={1}
                  max={120}
                  style={{ width: 70 }}
                  disabled={!canOperate}
                  value={policy.windowMinutes}
                  onChange={(e) => setDraft({ ...policy, windowMinutes: Number(e.target.value) })}
                />
                {(!Number.isInteger(policy.windowMinutes) ||
                  policy.windowMinutes < 1 ||
                  policy.windowMinutes > 120) && (
                  <div role="alert" style={{ color: 'var(--danger)' }}>
                    1〜120 分の整数を入力してください。
                  </div>
                )}
              </div>
              分以内に
              <div className="field">
                <label htmlFor="policy-count">回数</label>
                <input
                  id="policy-count"
                  className="input"
                  type="number"
                  aria-label="回数"
                  min={1}
                  max={10}
                  style={{ width: 60 }}
                  disabled={!canOperate}
                  value={policy.maxRestarts}
                  onChange={(e) => setDraft({ ...policy, maxRestarts: Number(e.target.value) })}
                />
                {(!Number.isInteger(policy.maxRestarts) || policy.maxRestarts < 1 || policy.maxRestarts > 10) && (
                  <div role="alert" style={{ color: 'var(--danger)' }}>
                    1〜10 回の整数を入力してください。
                  </div>
                )}
              </div>
              回クラッシュしたら、あきらめて停止のままにする
            </div>
          )}
          <div className="row small">
            <div className="field">
              <label htmlFor="policy-idle-vm">VM を止めるまで（分）</label>
              <input
                id="policy-idle-vm"
                className="input"
                type="number"
                aria-label="VM を止めるまで（分）"
                min={0}
                max={1440}
                style={{ width: 80 }}
                disabled={!canOperate}
                value={policy.idleVmShutdownMinutes}
                onChange={(e) => setDraft({ ...policy, idleVmShutdownMinutes: Number(e.target.value) })}
              />
              {!idleVmMinutesValid(policy.idleVmShutdownMinutes) && (
                <div role="alert" style={{ color: 'var(--danger)' }}>
                  0〜1440 分の整数を入力してください。
                </div>
              )}
            </div>
            分たっても Minecraft が止まったままなら、VM も止めてメモリを空ける
          </div>
          <div className="small faint" style={{ paddingLeft: 26 }}>
            0 にすると VM は止めません。止めたあとも、起動すれば VM ごと立ち上がります。VM
            を止めている間はファイルの編集ができません。
          </div>
          {canOperate && draft && (
            <div className="row">
              <span className="spacer" />
              <button className="btn ghost small" onClick={() => setDraft(undefined)}>
                元に戻す
              </button>
              <button
                className="btn primary small"
                disabled={
                  !Number.isInteger(policy.windowMinutes) ||
                  policy.windowMinutes < 1 ||
                  policy.windowMinutes > 120 ||
                  !Number.isInteger(policy.maxRestarts) ||
                  policy.maxRestarts < 1 ||
                  policy.maxRestarts > 10 ||
                  !idleVmMinutesValid(policy.idleVmShutdownMinutes)
                }
                onClick={() => void save()}
              >
                保存
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function ScheduleTab({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const tasks = usePoll(() => apiM.tasks(server.id), 30000, [server.id]);
  const [editing, setEditing] = useState<ScheduledTask | 'new'>();
  const [running, setRunning] = useState<string>();

  const remove = async (task: ScheduledTask) => {
    if (!window.confirm(`スケジュール「${task.name}」を削除しますか？`)) return;
    try {
      await apiM.deleteTask(server.id, task.id);
      await tasks.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const runNow = async (task: ScheduledTask) => {
    const what = { restart: '今すぐ再起動', command: '今すぐコマンドを実行', snapshot: '今すぐスナップショットを取る' }[
      task.kind
    ];
    const note = task.kind === 'restart' ? '（予告は省略します）' : '';
    if (!window.confirm(`「${task.name}」を${what}しますか？${note}`)) return;
    setRunning(task.id);
    try {
      const { result } = await apiM.runTask(server.id, task.id);
      notify(runResultLabel[result].label, result === 'failed');
      await tasks.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setRunning(undefined);
    }
  };

  const list = tasks.data ?? [];
  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <div className="card">
        <div className="card-header">
          <h2>スケジュール</h2>
          {canOperate && (
            <button className="btn small" onClick={() => setEditing('new')}>
              追加
            </button>
          )}
        </div>
        <ErrorAlert error={tasks.error} />
        {list.length === 0 ? (
          <Empty>
            スケジュールはありません。毎日のスナップショットで、壊れたときに前の日に戻せるようにしておくのがおすすめです。Mod
            サーバーはメモリが少しずつ増えることがあるので、毎日の再起動もおすすめします。
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>名前</th>
                  <th>いつ</th>
                  <th>内容</th>
                  <th>前回</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((task) => (
                  <tr key={task.id} className={task.enabled ? undefined : 'unchanged'}>
                    <td>
                      {task.name} {!task.enabled && <span className="tag">無効</span>}
                    </td>
                    <td className="small">
                      {daysText(task.days)} {task.time}
                      {task.nextRunAt && <div className="faint">次回 {formatDateTime(task.nextRunAt)}</div>}
                    </td>
                    <td className="small">
                      {task.kind === 'snapshot' ? (
                        <>
                          スナップショット
                          <div className="faint">{task.keep} 個まで残す</div>
                        </>
                      ) : task.kind === 'restart' ? (
                        <>
                          再起動
                          <div className="faint">
                            {task.warnMinutes.length > 0 ? `${task.warnMinutes.join('・')} 分前に予告` : '予告なし'}
                            {task.skipIfPlayersOnline && '・プレイヤーがいれば見送り'}
                          </div>
                        </>
                      ) : (
                        <div className="mono" style={{ whiteSpace: 'pre-wrap' }}>
                          {task.commands.map((c) => `/${c}`).join('\n')}
                        </div>
                      )}
                    </td>
                    <td className="small">
                      {task.lastRun ? (
                        <>
                          <Badge tone={runResultLabel[task.lastRun.result].tone}>
                            {runResultLabel[task.lastRun.result].label}
                          </Badge>
                          <div className="faint">{formatDateTime(task.lastRun.at)}</div>
                        </>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {canOperate && (
                        <>
                          <button
                            className="btn small"
                            disabled={running === task.id}
                            onClick={() => void runNow(task)}
                          >
                            今すぐ実行
                          </button>{' '}
                          <button className="btn small" onClick={() => setEditing(task)}>
                            編集
                          </button>{' '}
                          <button className="btn ghost small danger" onClick={() => void remove(task)}>
                            削除
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="small faint" style={{ margin: 0 }}>
        時刻は日本時間です。再起動の予告は「[mc-panel] N 分後にサーバーを再起動します」とゲーム内に流れます。
      </p>
      <PolicyCard serverId={server.id} />
      {editing && (
        <TaskEditor
          serverId={server.id}
          initial={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void tasks.reload();
          }}
        />
      )}
    </div>
  );
}
